// RoleEditorPage — three modes on one layout:
//   new  (/roles/new)   → editable name/description + empty matrix → POST /roles.
//   edit (/roles/:id)   → custom (non-default, non-owner) roles open editable;
//                         dirty shows Discard/Save → PATCH /roles/:id.
//   view (/roles/:id)   → system/owner roles stay read-only.
// The matrix structure comes from GET /modules; enabled cells from GET /roles/:id.
// Toggling applies the cascade rules (toggleWithCascade).
//
// Two independent questions decide what renders: whether the role is STRUCTURALLY
// modifiable (system/owner roles never are, whatever the user holds) and whether
// the user holds the verb — `create` on /roles/new, `edit` on an existing role,
// `delete` for removal. `delete` is deliberately independent of `edit`: a role
// holding only delete gets a read-only editor with a working Delete button.

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ConfirmModal,
  Field,
  Icon,
  Input,
  SegmentedControl,
  Textarea,
} from '@/components/common';
import {
  buildRolePayload,
  enabledSet,
  toggleWithCascade,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useGetModulesQuery,
  useGetRoleQuery,
  useUpdateRoleMutation,
  type MatrixModule,
  type Platform,
} from '@/api/endpoints/rolesApi';
import useModuleAccess from '@/hooks/useModuleAccess';
import CapabilityMatrix from './CapabilityMatrix';

/** Count of selected permission ids across a platform's modules. */
const countSelected = (modules: MatrixModule[], selected: Set<string>) =>
  modules.reduce(
    (n, m) => n + [...m.verbs, ...m.extras].filter((p) => selected.has(p.id)).length,
    0,
  );

const RoleEditorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleId = '' } = useParams();
  const isNew = !!useMatch('/roles/new');

  const { canCreate, canEdit, canDelete } = useModuleAccess('roles');

  const modulesQ = useGetModulesQuery();
  const roleQ = useGetRoleQuery(roleId, { skip: !roleId || isNew });
  const [createRole, { isLoading: creating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: deleting }] = useDeleteRoleMutation();

  const [platform, setPlatform] = useState<Platform>('web');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);

  const role = roleQ.data;
  const catalog = modulesQ.data;

  // Structural: system and owner roles are immutable whatever the user holds.
  const modifiable = isNew || (!!role && !role.isDefault && !role.isOwner);
  // Creating needs `create`, changing an existing role needs `edit`. Keeping the
  // name means the form, matrix, subtitle and save block need no other change.
  const editable = modifiable && (isNew ? canCreate : canEdit);
  const canRemove = modifiable && !isNew && canDelete;

  // Seed the drafts (and the dirty baseline) from the loaded role.
  const seed = useMemo(
    () =>
      isNew || !role
        ? { name: '', description: '', ids: new Set<string>() }
        : { name: role.name, description: role.description, ids: enabledSet(role) },
    [isNew, role],
  );
  useEffect(() => {
    setName(seed.name);
    setDescription(seed.description);
    setSelected(new Set(seed.ids));
  }, [seed]);

  const selectedKey = useMemo(() => [...selected].sort().join('|'), [selected]);
  const dirty =
    name !== seed.name ||
    description !== seed.description ||
    selectedKey !== [...seed.ids].sort().join('|');

  const loading = modulesQ.isLoading || roleQ.isLoading;
  const error = modulesQ.isError || roleQ.isError;

  const modules = (platform === 'web' ? catalog?.web : catalog?.mobile) ?? [];
  const isEnabled = (permId: string) => selected.has(permId);
  const onToggle = (module: MatrixModule, permId: string) =>
    setSelected((s) => toggleWithCascade(s, module, permId, modules));

  const webCount = useMemo(() => countSelected(catalog?.web ?? [], selected), [catalog, selected]);
  const mobileCount = useMemo(
    () => countSelected(catalog?.mobile ?? [], selected),
    [catalog, selected],
  );
  const activeCount = platform === 'web' ? webCount : mobileCount;

  const submit = async () => {
    if (!catalog || !name.trim()) return;
    const body = buildRolePayload(catalog, selected, name, description);
    try {
      if (isNew) {
        await createRole(body).unwrap();
        toast.success(t('roles_created', { name: body.name }));
        navigate('/roles');
      } else {
        await updateRole({ id: roleId, body }).unwrap();
        toast.success(t('roles_saved'));
      }
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const discard = () => {
    setName(seed.name);
    setDescription(seed.description);
    setSelected(new Set(seed.ids));
  };

  const remove = async () => {
    try {
      await deleteRole(roleId).unwrap();
      toast.success(t('roles_deleted'));
      navigate('/roles');
    } catch {
      toast.error(t('error_generic'));
    }
  };

  if (!roleId && !isNew) return <Navigate to='/roles' replace />;
  // Sub-routes ride on verb-blind module access (App.tsx), so /roles/new is
  // registered for anyone who can reach Roles at all — and the isNew action block
  // below renders "Create role" outside the `editable` check. Bounce before
  // anything paints rather than showing a form that cannot be submitted.
  if (isNew && !canCreate) return <Navigate to='/roles' replace />;

  const saving = creating || updating;

  return (
    <div className='roles-editor'>
      <button type='button' className='roles-editor__back' onClick={() => navigate('/roles')}>
        <Icon name='arrow-left' size={16} />
        {t('roles_all_roles')}
      </button>

      <Card
        head={
          <div className='roles-editor__title-row'>
            <h1 className='roles-editor__name'>
              {isNew ? name.trim() || t('roles_new_title') : (role?.name ?? '')}
            </h1>
            {role?.isDefault && (
              <span className='roles-editor__badge'>
                <Icon name='lock' size={13} />
                {t('roles_system_badge')}
              </span>
            )}
            {canRemove && (
              <Button
                size='sm'
                variant='danger'
                iconStart='trash-2'
                onClick={() => setConfirmDelete(true)}
                disabled={saving || deleting}
              >
                {t('roles_delete')}
              </Button>
            )}
          </div>
        }
      >
        <div className='roles-editor__toolbar'>
          {role?.isDefault && (
            <div className='roles-editor__locked'>
              <Icon name='lock' size={15} />
              {t('roles_system_locked')}
            </div>
          )}
          {role?.isOwner && (
            <div className='roles-editor__owner'>
              <Icon name='crown' size={15} />
              {t('roles_owner_note')}
            </div>
          )}

          {editable && (
            <div className='roles-editor__form'>
              <Field label={t('roles_field_name')}>
                <Input
                  value={name}
                  placeholder={t('roles_name_placeholder')}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field label={t('roles_field_desc')}>
                <Textarea
                  rows={2}
                  value={description}
                  placeholder={t('roles_desc_placeholder')}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
            </div>
          )}

          <div className='roles-editor__counts'>
            <span className='roles-editor__count'>
              <Icon name='monitor' size={15} />
              <b>{webCount}</b> {t('roles_web')}
            </span>
            <span className='roles-editor__count'>
              <Icon name='smartphone' size={15} />
              <b>{mobileCount}</b> {t('roles_mobile')}
            </span>
          </div>
        </div>
      </Card>

      <Card
        padded={false}
        className='roles-editor__access'
        head={
          <div>
            <h2 className='roles-editor__access-title'>{t('roles_access_title')}</h2>
            <p className='roles-editor__access-sub'>
              {editable ? t('roles_access_subtitle_edit') : t('roles_access_subtitle')}
            </p>
          </div>
        }
      >
        <div className='roles-editor__access-toolbar'>
          <SegmentedControl
            aria-label={t('roles_access_title')}
            value={platform}
            onChange={(v) => setPlatform(v as Platform)}
            options={[
              { value: 'web', label: t('roles_web_app') },
              { value: 'mobile', label: t('roles_mobile_app') },
            ]}
          />
          <span className='roles-editor__enabled'>
            <Icon name={platform === 'web' ? 'monitor' : 'smartphone'} size={15} />
            {t('roles_enabled_on', {
              count: activeCount,
              platform: platform === 'web' ? t('roles_web_app') : t('roles_mobile_app'),
            })}
          </span>
        </div>

        {loading ? (
          <div className='roles-editor__empty'>{t('text_loading')}</div>
        ) : error ? (
          <div className='roles-editor__empty'>{t('error_generic')}</div>
        ) : modules.length === 0 ? (
          <div className='roles-editor__empty'>
            <Icon name='smartphone' size={26} />
            <p>{t('roles_mobile_empty')}</p>
          </div>
        ) : (
          <CapabilityMatrix
            modules={modules}
            isEnabled={isEnabled}
            editable={editable}
            onToggle={onToggle}
          />
        )}
      </Card>

      {/* ---- actions ---- */}
      {isNew ? (
        <div className='roles-editor__actions'>
          <div className='roles-editor__actions-end'>
            <Button variant='secondary' onClick={() => navigate('/roles')} disabled={saving}>
              {t('button_cancel')}
            </Button>
            <Button iconStart='plus' onClick={submit} loading={saving} disabled={!name.trim()}>
              {t('roles_create')}
            </Button>
          </div>
        </div>
      ) : editable ? (
        <div className='roles-editor__actions'>
          <div className='roles-editor__actions-end'>
            {dirty ? (
              <>
                <Button variant='secondary' onClick={discard} disabled={saving}>
                  {t('roles_discard')}
                </Button>
                <Button iconStart='check' onClick={submit} loading={saving} disabled={!name.trim()}>
                  {t('roles_save')}
                </Button>
              </>
            ) : (
              <span className='roles-editor__saved'>
                <Icon name='check' size={15} />
                {t('roles_saved_badge')}
              </span>
            )}
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        tone='danger'
        icon='trash-2'
        title={t('roles_delete_confirm_title', { name: role?.name ?? '' })}
        description={t('roles_delete_confirm_body')}
        confirmLabel={t('roles_delete_confirm')}
        confirmVariant='danger'
        loading={deleting}
      />
    </div>
  );
};

export default RoleEditorPage;
